import { useState, useEffect, useRef, useMemo } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import uniq from 'lodash/uniq'
import { useDispatch } from 'react-redux'
import uuid from 'uuid/v4'
import { Paper } from '@components'
import { INNER_PAGE_HEIGHT } from '@components/layout'
import { TableHeader } from '@datasets/components/table-header'
import * as DatasetActions from '@datasets/datasets.actions'
import { SubmissionTable } from '@datasets/handsontable/submission-table/submission-table'
import { api } from 'api'
import { DataContainer } from 'dataContainer/data-container'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import * as TemplateActions from './template.actions'
import { t } from '@lingui/core/macro'

export const Preview = props => {
  const dispatch = useDispatch()
  const containerRef = useRef(null)
  const [submissionTemplate, setSubmissionTemplate] = useState(null)
  const [tableRef, setTableRef] = useState(null)
  const [binderRef, setBinderRef] = useState(null)
  const [dataset] = useState({ uuid: uuid() })
  const [errorCount, setErrorCount] = useState(0)
  const [errors, setErrors] = useState([])

  useEffect(() => {
    const previewTemplate = TemplateActions.transformTemplate(
      JSON.parse(JSON.stringify(props.template)),
    )
    const listIds = uniq(
      previewTemplate.template_fields
        .map(tf => {
          if (tf.list_governance) {
            return tf.list_governance.list_id
          }
          if (tf.field.list_governance) {
            return tf.field.list_governance.list_id
          }
        })
        .filter(tf => !!tf),
    )

    api.post('/api/v1/account/template/preview-dependencies', listIds).then(({ data }) => {
      const transformedColumns = DatasetActions.transformDeps(
        JSON.parse(JSON.stringify(previewTemplate.template_fields)),
        data.reduce((memo, item) => {
          memo[item.id] = item
          return memo
        }, {}),
      )
      data.transformedColumns = transformedColumns
      setSubmissionTemplate(data)
      UrlAudits.set([])
      UrlAudits.clearQueue()
    })

    return function () {
      dispatch(DatasetActions.resetStore())
    }
  }, [])

  const dataContainer = useMemo(() => {
    const dc = new DataContainer(submissionTemplate ? submissionTemplate.transformedColumns : [])
    if (dataset.uuid) {
      dc.datasetUuid = dataset.uuid
    }
    return dc
  }, [submissionTemplate])

  function onUserTableInput() {}

  function onInvalidCellsUpdated(errors) {
    setErrorCount(errors.length)
    setErrors(errors)
  }

  const onErrorClick = () => {
    const countColumns = tableRef.countCols()
    const invalidCells = errors
    invalidCells.sort((a, b) =>
      a.metadata.row * countColumns + binderRef.visibleRenderColumnsOrderById[a.field_id] <
      b.metadata.row * countColumns + binderRef.visibleRenderColumnsOrderById[b.field_id]
        ? -1
        : 1,
    )
    const currentSelection = tableRef.getSelectedLast()
    let toSelect
    if (!currentSelection) {
      toSelect = invalidCells[0]
    } else {
      const [startRow, startCol] = currentSelection
      const currentIdx = startRow * countColumns + startCol
      const nextCell = invalidCells.find(invalidCell => {
        const idxForCell =
          invalidCell.metadata.row * countColumns +
          binderRef.visibleRenderColumnsOrderById[invalidCell.field_id]
        return idxForCell > currentIdx
      })
      toSelect = nextCell || invalidCells[0]
    }
    toSelect &&
      tableRef.selectCell(
        toSelect.metadata.row,
        binderRef.visibleRenderColumnsOrderById[toSelect.field_id],
      )
  }

  const memoedTable = useMemo(
    () => (
      <SubmissionTable
        dataContainer={dataContainer}
        dataset={dataset}
        template={submissionTemplate}
        hasUnsavedChanges={false}
        onUserChange={onUserTableInput}
        onInvalidCellsUpdated={onInvalidCellsUpdated}
        setTableRef={setTableRef}
        setBinderRef={setBinderRef}
      />
    ),
    [submissionTemplate, dataContainer],
  )

  const lastSavedText = ''

  return (
    <motion.div
      className="create-dataset"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ height: 'calc(100vh - 64px)' }}
    >
      <Paper
        elevation={3}
        style={{ overflow: 'hidden', display: 'flex', height: '100%', flexDirection: 'column' }}
      >
        <div
          style={{ height: 80, minHeight: 80, flexShrink: 1, display: 'flex', padding: '0 24px' }}
        >
          <div
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          >
            <Typography variant="h6">
              {t`Template Preview - (Note: previously submitted values and suggestion lists are not available for preview)`}
            </Typography>
          </div>
          <div
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <div>
              <Fab size="small" aria-label="Close" onClick={props.onClose}>
                <CloseIcon />
              </Fab>
            </div>
          </div>
        </div>
        <div style={{ flexGrow: 1 }}>
          <TableHeader
            dataset={dataset}
            template={submissionTemplate}
            lastSavedText={lastSavedText}
            errors={errors}
            errorCount={errorCount}
            onErrorClick={onErrorClick}
            tableRef={tableRef}
          />
          <div
            id="HOTContainer"
            style={{ width: '100%', height: INNER_PAGE_HEIGHT, position: 'relative' }}
            className="submission-table"
            ref={containerRef}
          >
            {' '}
            <div
              id="handsontable-container"
              className="handsontable-container"
              style={{ height: '100%' }}
            >
              {submissionTemplate ? (
                memoedTable
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress color="primary" />
                </div>
              )}
            </div>
          </div>
        </div>
      </Paper>
    </motion.div>
  )
}
